import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const nationalityKeys = {
  all: ['nationalities'],
  lists: () => [...nationalityKeys.all, 'list'],
  details: () => [...nationalityKeys.all, 'detail'],
  detail: (id) => [...nationalityKeys.details(), id],
};

/**
 * Hook para obtener todas las nacionalidades
 */
export const useNationalities = () => {
  return useQuery({
    queryKey: nationalityKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('nationalities');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener una nacionalidad específica
 * @param {string} id - UUID de la nacionalidad
 * @param {Object} options - Opciones adicionales para la query
 */
export const useNationality = (id, options = {}) => {
  return useQuery({
    queryKey: nationalityKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`nationalities/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear una nueva nacionalidad
 */
export const useCreateNationality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nationalityData) => {
      const response = await apiClient.post('nationalities', nationalityData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nationalityKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating nationality:', error);
    },
  });
};

/**
 * Hook para actualizar una nacionalidad existente
 */
export const useUpdateNationality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (nationalityData) => {
      const response = await apiClient.put(
        `nationalities/${nationalityData.uuid}`,
        nationalityData
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(nationalityKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: nationalityKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating nationality:', error);
    },
  });
};

/**
 * Hook para eliminar una nacionalidad
 */
export const useDeleteNationality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`nationalities/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: nationalityKeys.lists() });
      queryClient.removeQueries({ queryKey: nationalityKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting nationality:', error);
    },
  });
};
